import { useCallback, useRef } from 'react';

import { CustomerViewModel } from '@models/Customer';

/**
 * Options for the tax calculation hook
 */
export interface UseTaxCalculationOptions {
  /** Initial tax exempt status */
  initialTaxExempt?: boolean;
}

/**
 * Hook for managing tax exempt status and providing tax calculation helpers.
 * Works with formik to track the current tax exempt status.
 */
export const useTaxCalculation = (options: UseTaxCalculationOptions = {}) => {
  const { initialTaxExempt = false } = options;

  // Ref to store current formik props for access in helper functions
  const formikPropsRef = useRef<any>(null);

  /**
   * Sets the formik props reference for accessing current values
   */
  const setFormikPropsRef = useCallback((props: any) => {
    formikPropsRef.current = props;
  }, []);

  /**
   * Gets the current tax exempt status from formik values or fallback
   */
  const getIsTaxExempt = useCallback((): boolean => {
    return formikPropsRef.current?.values?.isTaxExempt ?? initialTaxExempt;
  }, [initialTaxExempt]);

  /**
   * Gets the tax exempt status from a customer, with optional override
   */
  const getCustomerTaxExemptStatus = useCallback(
    (
      customer: CustomerViewModel | null | undefined,
      override?: boolean
    ): boolean => {
      if (override !== undefined) {
        return override;
      }
      return customer?.isTaxExempt === true;
    },
    []
  );

  /**
   * Updates the tax exempt status in formik
   */
  const setTaxExemptStatus = useCallback((isTaxExempt: boolean) => {
    if (formikPropsRef.current) {
      formikPropsRef.current.setFieldValue('isTaxExempt', isTaxExempt);
    }
  }, []);

  /**
   * Handles customer change and updates tax exempt status accordingly
   */
  const handleCustomerTaxExemptChange = useCallback(
    (customer: CustomerViewModel | null): boolean => {
      const isTaxExempt = customer?.isTaxExempt === true;
      setTaxExemptStatus(isTaxExempt);
      return isTaxExempt;
    },
    [setTaxExemptStatus]
  );

  return {
    // Ref management
    formikPropsRef,
    setFormikPropsRef,

    // Tax exempt status
    getIsTaxExempt,
    getCustomerTaxExemptStatus,
    setTaxExemptStatus,
    handleCustomerTaxExemptChange,
  };
};
